import type { Lesson } from "@/core/types";
import { criterion, fromCriteria, unsupported } from "@/core/check";
import { dateiendung, hatEigeneVorlage, readEigenschaften } from "@/office/word/pruefen";

export const lektionDokumentvorlagen: Lesson = {
  id: "dokumentvorlagen",
  title: "Dokumentvorlagen (.dotx)",
  summary: "Einmal aufsetzen, immer wieder verwenden.",
  durationMinutes: 9,
  learningGoals: [
    "Den Unterschied zwischen Dokument und Vorlage verstehen",
    "Eine eigene Dokumentvorlage speichern",
    "Vorlagen im Team bereitstellen",
  ],
  steps: [
    {
      id: "vorlage-speichern",
      title: "Eine eigene Vorlage anlegen",
      explanation: [
        "Fast jede Organisation hat wiederkehrende Dokumente: Angebote, Protokolle, " +
          "Berichte. Der verbreitete Umgang damit ist, ein altes Dokument zu öffnen, den " +
          "Inhalt zu löschen und neu zu schreiben. Das geht meistens gut – bis jemand " +
          "vergisst, den Kundennamen im Betreff auszutauschen.",
        "Eine Dokumentvorlage macht daraus einen sauberen Vorgang. Du baust das Dokument " +
          "einmal komplett auf – mit Formatvorlagen, Kopf- und Fußzeile, Logo, " +
          "Platzhaltertexten – und speicherst es über „Datei“ → „Speichern unter“ mit dem " +
          "Dateityp „Word-Vorlage (*.dotx)“.",
        "Der Unterschied im Verhalten ist der entscheidende Punkt: Ein Doppelklick auf " +
          "eine .dotx-Datei öffnet nicht die Vorlage, sondern eine unbenannte Kopie " +
          "davon. Das Original kann gar nicht versehentlich überschrieben werden.",
        "Speicherst du die Vorlage im Ordner „Benutzerdefinierte Office-Vorlagen“, " +
          "erscheint sie unter „Datei“ → „Neu“ neben den Word-Vorlagen. Word schlägt " +
          "diesen Ordner beim Speichern als .dotx automatisch vor.",
      ],
      keyPoints: [
        "Speichern unter → Dateityp „Word-Vorlage (*.dotx)“.",
        "Ein Doppelklick auf eine Vorlage öffnet immer eine neue Kopie.",
        "Im Ordner „Benutzerdefinierte Office-Vorlagen“ erscheint sie unter Datei → Neu.",
      ],
      task: {
        instruction:
          "Speichere dein Übungsdokument als Word-Vorlage (.dotx). Erstelle dann per " +
          "Doppelklick auf die Vorlage ein neues Dokument und öffne dort dieses Kurs-Panel.",
        hint: "Datei → Speichern unter → Dateityp „Word-Vorlage (*.dotx)“. Danach die .dotx im Explorer doppelt anklicken.",
        check: async () => {
          const eigenschaften = await readEigenschaften();
          const endung = dateiendung();
          if (eigenschaften === null && endung === null) {
            return unsupported(
              "Diese Word-Version stellt dem Add-in weder die Vorlage noch den Dateinamen zur Verfügung.",
              "Prüfe unter Datei → Optionen → Add-Ins → Vorlagen, welche Vorlage verbunden ist. Danach kannst du den Schritt selbst als erledigt markieren.",
            );
          }
          const ausVorlage = eigenschaften !== null && hatEigeneVorlage(eigenschaften);
          return fromCriteria(
            [
              criterion(
                ausVorlage
                  ? `Das Dokument beruht auf der Vorlage „${eigenschaften?.template}“`
                  : "Das Dokument beruht auf einer eigenen Vorlage statt auf Normal.dotm",
                ausVorlage || endung === "dotx",
              ),
            ],
            {
              success:
                endung === "dotx"
                  ? "Du bearbeitest gerade die Vorlage selbst. Ein Doppelklick darauf erzeugt ab jetzt immer eine Kopie."
                  : "Ein neues Dokument aus deiner Vorlage – das Original bleibt unberührt.",
              failure: "Das Dokument beruht noch auf der Standardvorlage Normal.dotm.",
              hint: "Nicht die .dotx über Datei → Öffnen öffnen, sondern im Explorer doppelt anklicken.",
            },
          );
        },
      },
    },
    {
      id: "im-team",
      title: "Vorlagen im Team",
      explanation: [
        "Eine Vorlage auf dem eigenen Rechner hilft nur einem. Für ein Team gibt es " +
          "mehrere Wege, mit unterschiedlichem Aufwand.",
        "Am einfachsten ist ein gemeinsamer Netzwerk- oder SharePoint-Ordner, den alle " +
          "kennen. Unter „Datei“ → „Optionen“ → „Erweitert“ → „Dateispeicherorte“ lässt " +
          "sich zusätzlich ein Pfad für „Arbeitsgruppenvorlagen“ eintragen – Vorlagen von " +
          "dort erscheinen bei allen automatisch unter „Datei“ → „Neu“.",
        "In größeren Organisationen mit Microsoft 365 übernimmt das die " +
          "Organisationsvorlagen-Funktion, die eine Dokumentbibliothek zentral " +
          "bereitstellt.",
        "Ein Hinweis zum Aktualisieren: Bereits erstellte Dokumente ziehen Änderungen an " +
          "der Vorlage nicht automatisch nach. Wer eine Vorlage überarbeitet, sollte das " +
          "im Team ankündigen – laufende Dokumente behalten den alten Stand.",
      ],
      keyPoints: [
        "Gemeinsamer Ordner oder Pfad für Arbeitsgruppenvorlagen.",
        "Datei → Optionen → Erweitert → Dateispeicherorte.",
        "Bestehende Dokumente übernehmen Vorlagenänderungen nicht rückwirkend.",
      ],
    },
  ],
};
